import { useRouter } from 'next/router';
import React, { useEffect, useState } from 'react';
import { useForm } from 'react-hook-form';
import { useTranslation } from 'react-i18next';
import { sendContactForm } from '../../fetchers/contact';
import { validateRecaptchaToken } from '../../fetchers/recaptcha';
import { requestGuestReturn } from '../../fetchers/return';
import ContactMenu from '../../components/ContactMenu';
import Footer from '../../components/Footer';
import FormErrorMessage from '../../components/FormErrorMessage';
import FormInput from '../../components/FormInput';
import FormTextArea from '../../components/FormTextArea';
import Menu from '../../components/Menu/Menu';
import SecondaryButtonBig from '../../components/SecondaryButtonBig';
import Spinner from '../../components/Spinner';
import withRecaptcha from '../../components/withRecaptcha';
import { useAlert } from '../../context/AlertProvider';
import { getToken } from '../../helpers/auth';
import { getLangCountry } from '../../helpers/languages';
import getRecaptchaToken from '../../utils/getRecaptchaToken';
import MyHead from '../../components/MyHead';

interface IPressContactsData {
  first_name: string;
  last_name: string;
  phone: string;
  press: string;
  country: string;
  website: string;
  email: string;
  message: string;
  newsletter: boolean;
  contact: boolean;
}

const PressContacts: React.FC = () => {
  const alert = useAlert();
  const token = getToken();
  const { t } = useTranslation();
  const [loading, setLoading] = useState(false);

  const {
    formState: { errors },
    register,
    handleSubmit,
    reset,
    watch,
    setValue
  } = useForm<IPressContactsData>({
    defaultValues: {
      first_name: '',
      last_name: '',
      phone: '',
      press: '',
      country: '',
      website: '',
      email: '',
      message: ''
    }
  });

  useEffect(() => {
    register('message', {
      required: t('REQUIRED_FIELD') as string
    });
  }, []);

  const onSubmit = async (values: IPressContactsData, event: any) => {
    try {
      setLoading(true);

      // Validate recaptcha
      await validateRecaptchaToken({
        token,
        recaptchaToken: await getRecaptchaToken()
      });

      await sendContactForm({ token, formName: 'press', data: values });

      reset();
      event.target.reset();
      alert.show(t('SEND_FORM_SUCCESS'));
    } catch (error: any) {
      alert.error(t('GENERIC_ERROR'));
    } finally {
      setLoading(false);
    }
  };

  const values = watch();

  return (
    <>
      <MyHead />
      <Menu />
      <div className="py-12 bg-gray-100">
        <div className="max-w-5xl mx-auto px-6">
          <ContactMenu actual_page={'press-contacts'} />
          <div className="bg-white py-8 px-6 rounded-lg mb-6">
            <div className="flex items-left justify-between mb-3 flex-col md:flex-row md:items-center">
              <h3 className="text-3xl font-sans">{t('CONTACT_US_PRESS')}</h3>
            </div>

            <form onSubmit={handleSubmit(onSubmit)}>
              <div className="flex space-x-6">
                <div className="w-full">
                  <FormInput
                    id="first_name"
                    type="text"
                    placeholder={t('NAME')}
                    invalid={Boolean(errors.first_name)}
                    {...{
                      ...register('first_name', {
                        required: t('REQUIRED_FIELD') as string
                      })
                    }}
                  />

                  <FormErrorMessage errors={errors} name="first_name" />
                </div>
                <div className="w-full">
                  <FormInput
                    id="last_name"
                    type="text"
                    placeholder={t('SURNAME')}
                    invalid={Boolean(errors.last_name)}
                    {...{
                      ...register('last_name', {
                        required: t('REQUIRED_FIELD') as string
                      })
                    }}
                  />

                  <FormErrorMessage errors={errors} name="last_name" />
                </div>
              </div>

              <div className="flex space-x-6">
                <div className="w-full">
                  <FormInput
                    id="email"
                    type="email"
                    placeholder={t('MAIL_ADDRESS')}
                    invalid={Boolean(errors.email)}
                    {...register('email', {
                      required: t('REQUIRED_FIELD') as string,
                      pattern: {
                        value: /^[A-Z0-9._%+-]+@[A-Z0-9.-]+\.[A-Z]{2,10}$/i,
                        message: t('INVALID_EMAIL')
                      }
                    })}
                  />

                  <FormErrorMessage errors={errors} name="email" />
                </div>
                <div className="w-full">
                  <FormInput
                    id="phone"
                    type="text"
                    placeholder={t('PHONE')}
                    invalid={Boolean(errors.phone)}
                    {...{
                      ...register('phone', {
                        required: t('REQUIRED_FIELD') as string
                      })
                    }}
                  />

                  <FormErrorMessage errors={errors} name="phone" />
                </div>
              </div>

              <div className="flex space-x-6">
                <div className="w-full">
                  <FormInput
                    id="press"
                    type="text"
                    placeholder={t('PRESS_HEADING')}
                    invalid={Boolean(errors.press)}
                    {...{
                      ...register('press', {
                        required: t('REQUIRED_FIELD') as string
                      })
                    }}
                  />

                  <FormErrorMessage errors={errors} name="press" />
                </div>
                <div className="w-full">
                  <FormInput
                    id="country"
                    type="text"
                    placeholder={t('COUNTRY')}
                    invalid={Boolean(errors.country)}
                    {...{
                      ...register('country', {
                        required: t('REQUIRED_FIELD') as string
                      })
                    }}
                  />

                  <FormErrorMessage errors={errors} name="country" />
                </div>
              </div>

              <div>
                <div>
                  <FormInput
                    id="website"
                    type="text"
                    placeholder={t('WEBSITE')}
                    invalid={Boolean(errors.website)}
                    {...{
                      ...register('website', {
                        required: t('REQUIRED_FIELD') as string
                      })
                    }}
                  />

                  <FormErrorMessage errors={errors} name="website" />
                </div>
              </div>

              <div>
                <FormTextArea
                  id="message"
                  placeholder={t('MESSAGE')}
                  invalid={Boolean(errors.message)}
                  value={values.message}
                  onChange={({ currentTarget }) =>
                    setValue('message', currentTarget.value, {
                      shouldValidate: true
                    })
                  }
                />

                <FormErrorMessage errors={errors} name="message" />
              </div>

              <div
                className={`relative flex items-center mt-2 py-2 px-3 border rounded ${
                  errors.newsletter ? ' border-red-500' : 'border-transparent'
                }`}
              >
                <input
                  id="contact"
                  aria-describedby="privacy-description"
                  type="checkbox"
                  className="focus:outline-none h-4 w-4 text-primary border-gray-300 rounded"
                  {...register('contact', {
                    required: t('REQUIRED_FIELD') as string
                  })}
                />
                <div className="ml-2 text-sm">
                  <label htmlFor="contact">
                    <div
                      id="newsletter-description"
                      className="text-gray-500 cursor-pointer"
                    >
                      {t('FOOTER_NEWSLETTER_PRIVACY')}
                      <a
                        href={`https://www.iubenda.com/privacy-policy/${t(
                          'IUBENDA.POLICY_ID'
                        )}`}
                        className="iubenda-white no-brand iubenda-noiframe iub-body-embed text-primary"
                        title="Privacy Policy"
                        target="_blank"
                        rel="noreferrer"
                      >
                        {t('PRIVACY_TITLE')}
                      </a>
                    </div>
                  </label>
                </div>
              </div>
              <div className="mt-6 max-w-lg">
                {loading ? (
                  <Spinner />
                ) : (
                  <button type="submit" className="w-full">
                    <SecondaryButtonBig text={t('SEND')} />
                  </button>
                )}
              </div>
            </form>
          </div>
        </div>
      </div>
      <Footer />
    </>
  );
};

export default withRecaptcha(PressContacts);
